import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Container, Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, TextField, Select, MenuItem, Switch, Button,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Chip,
  InputAdornment, Alert, Snackbar, CircularProgress, Tooltip, FormControl,
  InputLabel, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  PersonOff as PersonOffIcon,
  PersonAdd as PersonAddIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { userManagementAPI } from '../services/api';
import AppHeader from './AppHeader';

const ROLES_SUPERADMIN = [
  { value: 'superadmin', label: 'Super Admin' },
  { value: 'admin', label: 'Admin' },
  { value: 'editor', label: 'Editor' },
  { value: 'transportation', label: 'Transportation' },
  { value: 'viewer', label: 'Viewer' },
  { value: 'attendance', label: 'Attendance' },
];

const ROLES_ADMIN = [
  { value: 'admin', label: 'Admin' },
  { value: 'editor', label: 'Editor' },
  { value: 'transportation', label: 'Transportation' },
  { value: 'viewer', label: 'Viewer' },
  { value: 'attendance', label: 'Attendance' },
];

const roleColor = (role) => {
  switch (role) {
    case 'superadmin': return 'secondary';
    case 'admin': return 'error';
    case 'editor': return 'primary';
    case 'transportation': return 'info';
    case 'attendance': return 'warning';
    default: return 'default';
  }
};

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

function formatDateTime(iso) {
  if (!iso) return 'Never';
  return new Date(iso).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

const UserManagement = () => {
  const { user: currentUser, logout, isSuperAdmin, allSites, userSiteIds } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newUser, setNewUser] = useState({ email: '', role: 'viewer', first_name: '', last_name: '', sites: [] });
  const [addError, setAddError] = useState('');
  const [addLoading, setAddLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const roleOptions = isSuperAdmin ? ROLES_SUPERADMIN : ROLES_ADMIN;

  const managableSites = useMemo(() => {
    if (isSuperAdmin) return allSites;
    return allSites.filter(s => userSiteIds.includes(s.id));
  }, [isSuperAdmin, allSites, userSiteIds]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const usersRes = await userManagementAPI.getUsers();
      setUsers(usersRes.data.results || usersRes.data || []);
    } catch (err) {
      console.error('Failed to load users:', err);
      setSnackbar({ open: true, message: 'Failed to load users', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const filteredUsers = useMemo(() => {
    if (!search.trim()) return users;
    const q = search.toLowerCase();
    return users.filter(u =>
      u.email?.toLowerCase().includes(q) ||
      u.first_name?.toLowerCase().includes(q) ||
      u.last_name?.toLowerCase().includes(q)
    );
  }, [users, search]);

  const handleRoleChange = async (userId, newRole) => {
    const prev = users.map(u => ({ ...u }));
    setUsers(us => us.map(u => u.id === userId ? { ...u, role: newRole } : u));
    try {
      await userManagementAPI.updateUser(userId, { role: newRole });
      setSnackbar({ open: true, message: 'Role updated', severity: 'success' });
    } catch (err) {
      setUsers(prev);
      const msg = err.response?.data?.detail || 'Failed to update role';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    }
  };

  const handleActiveToggle = async (userId, currentActive) => {
    const newActive = !currentActive;
    const prev = users.map(u => ({ ...u }));
    setUsers(us => us.map(u => u.id === userId ? { ...u, is_active: newActive } : u));
    try {
      await userManagementAPI.updateUser(userId, { is_active: newActive });
      setSnackbar({
        open: true,
        message: newActive ? 'User activated' : 'User deactivated',
        severity: 'success',
      });
    } catch (err) {
      setUsers(prev);
      setSnackbar({ open: true, message: 'Failed to update status', severity: 'error' });
    }
  };

  const handleSiteToggle = async (userId, siteId) => {
    const user = users.find(u => u.id === userId);
    if (!user) return;
    const currentSites = user.sites || [];
    const newSites = currentSites.includes(siteId)
      ? currentSites.filter(id => id !== siteId)
      : [...currentSites, siteId];

    const prev = users.map(u => ({ ...u }));
    setUsers(us => us.map(u =>
      u.id === userId ? { ...u, sites: newSites } : u
    ));
    try {
      await userManagementAPI.updateUser(userId, { sites: newSites });
      setSnackbar({ open: true, message: 'Sites updated', severity: 'success' });
    } catch (err) {
      setUsers(prev);
      setSnackbar({ open: true, message: 'Failed to update sites', severity: 'error' });
    }
  };

  const handleAddUser = async () => {
    setAddError('');
    if (!newUser.email.trim()) { setAddError('Email is required'); return; }
    setAddLoading(true);
    try {
      const payload = {
        email: newUser.email.trim().toLowerCase(),
        role: newUser.role,
        first_name: newUser.first_name.trim(),
        last_name: newUser.last_name.trim(),
        sites: newUser.sites,
      };
      await userManagementAPI.createUser(payload);
      setAddDialogOpen(false);
      setNewUser({ email: '', role: 'viewer', first_name: '', last_name: '', sites: [] });
      setSnackbar({ open: true, message: 'User created — they can now sign in with Microsoft SSO', severity: 'success' });
      await loadData();
    } catch (err) {
      const detail = err.response?.data;
      if (detail?.email) {
        setAddError(Array.isArray(detail.email) ? detail.email[0] : detail.email);
      } else if (detail?.detail) {
        setAddError(detail.detail);
      } else if (typeof detail === 'string') {
        setAddError(detail);
      } else {
        setAddError('Failed to create user');
      }
    } finally {
      setAddLoading(false);
    }
  };

  const toggleNewUserSite = (siteId) => {
    setNewUser(p => ({
      ...p,
      sites: p.sites.includes(siteId)
        ? p.sites.filter(id => id !== siteId)
        : [...p.sites, siteId],
    }));
  };

  const renderSiteToggles = (userSites, userId, disabled = false) => {
    const sitesToShow = managableSites;
    return (
      <ToggleButtonGroup size="small" sx={{ flexWrap: 'wrap', gap: 0.25 }}>
        {sitesToShow.map(s => {
          const active = (userSites || []).includes(s.id);
          return (
            <ToggleButton
              key={s.id}
              value={s.id}
              selected={active}
              disabled={disabled}
              onClick={() => userId ? handleSiteToggle(userId, s.id) : toggleNewUserSite(s.id)}
              sx={{
                px: 1, py: 0.25,
                fontSize: '0.7rem',
                lineHeight: 1.4,
                textTransform: 'none',
                '&.Mui-selected': { bgcolor: 'primary.main', color: '#fff', '&:hover': { bgcolor: 'primary.dark' } },
              }}
            >
              {s.name}
            </ToggleButton>
          );
        })}
      </ToggleButtonGroup>
    );
  };

  const canEditUser = (targetUser) => {
    if (isSuperAdmin) return true;
    if (targetUser.role === 'superadmin') return false;
    return true;
  };

  return (
    <Box>
      <AppHeader
        rightContent={
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', width: '100%' }}>
            <Typography variant="body2" sx={{ mr: 2 }}>
              {currentUser?.email} ({currentUser?.role})
            </Typography>
            <Button color="inherit" onClick={logout} sx={{ color: '#fff' }}>Logout</Button>
          </Box>
        }
      />

      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4">User Management</Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAddDialogOpen(true)}>
            Add User
          </Button>
        </Box>

        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search by email or name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
              endAdornment: search && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearch('')}><ClearIcon /></IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {filteredUsers.length} user{filteredUsers.length !== 1 ? 's' : ''}
              {search && ` matching "${search}"`}
            </Typography>

            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ '& th': { fontWeight: 700, backgroundColor: '#f5f5f5' } }}>
                    <TableCell>Email</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Sites</TableCell>
                    <TableCell align="center">Active</TableCell>
                    <TableCell>SSO Provider</TableCell>
                    <TableCell>Last Login</TableCell>
                    <TableCell>Joined</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredUsers.map((u) => {
                    const isSelf = u.id === currentUser?.id;
                    const editable = canEditUser(u) && !isSelf;
                    return (
                      <TableRow
                        key={u.id}
                        sx={{
                          opacity: u.is_active ? 1 : 0.55,
                          '&:hover': { backgroundColor: '#fafafa' },
                        }}
                      >
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {u.email}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {u.first_name || u.last_name
                            ? `${u.first_name || ''} ${u.last_name || ''}`.trim()
                            : '—'}
                        </TableCell>
                        <TableCell>
                          {!editable ? (
                            <Chip label={u.role} color={roleColor(u.role)} size="small" />
                          ) : (
                            <Select
                              value={u.role}
                              size="small"
                              variant="standard"
                              onChange={(e) => handleRoleChange(u.id, e.target.value)}
                              sx={{ minWidth: 100 }}
                            >
                              {roleOptions.map(r => (
                                <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
                              ))}
                            </Select>
                          )}
                        </TableCell>
                        <TableCell>
                          {renderSiteToggles(u.sites, u.id, isSelf)}
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title={isSelf ? "You can't deactivate yourself" : (u.is_active ? 'Deactivate' : 'Activate')}>
                            <span>
                              <Switch
                                checked={u.is_active}
                                disabled={isSelf}
                                onChange={() => handleActiveToggle(u.id, u.is_active)}
                                size="small"
                                color="success"
                              />
                            </span>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          {u.oauth_provider ? (
                            <Chip label={u.oauth_provider} size="small" variant="outlined" />
                          ) : (
                            <Typography variant="body2" color="text.secondary">Local</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {formatDateTime(u.last_login)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {formatDate(u.date_joined)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {filteredUsers.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                        <PersonOffIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                        <Typography color="text.secondary">
                          {search ? 'No users match your search' : 'No users found'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Container>

      {/* Add User Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonAddIcon /> Add User
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Pre-create an account so the user can sign in with their HASC Microsoft account on first visit.
          </Typography>
          {addError && <Alert severity="error" sx={{ mb: 2 }}>{addError}</Alert>}
          <TextField
            autoFocus
            fullWidth
            label="Email address"
            type="email"
            value={newUser.email}
            onChange={(e) => setNewUser(p => ({ ...p, email: e.target.value }))}
            sx={{ mb: 2 }}
            helperText="Must match their Microsoft / Azure AD email"
          />
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              fullWidth
              label="First name"
              value={newUser.first_name}
              onChange={(e) => setNewUser(p => ({ ...p, first_name: e.target.value }))}
            />
            <TextField
              fullWidth
              label="Last name"
              value={newUser.last_name}
              onChange={(e) => setNewUser(p => ({ ...p, last_name: e.target.value }))}
            />
          </Box>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Role</InputLabel>
            <Select
              value={newUser.role}
              label="Role"
              onChange={(e) => setNewUser(p => ({ ...p, role: e.target.value }))}
            >
              {roleOptions.map(r => (
                <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Site access</Typography>
          {renderSiteToggles(newUser.sites, null)}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAddUser}
            disabled={addLoading || !newUser.email.trim()}
            startIcon={addLoading ? <CircularProgress size={18} /> : <PersonAddIcon />}
          >
            Create User
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserManagement;
